import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirestoreDb } from '@/lib/firebase';
import { useUnsavedWarning } from '@/hooks/useUnsavedWarning';
import { useAsyncSave } from '@/components/settings/useAsyncSave';
import { useOrgSettingsDoc } from '@/components/settings/useOrgSettingsDoc';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { SettingsFirestoreError } from '@/components/settings/SettingsFirestoreError';
import { SettingsSection } from '@/components/settings/SettingsSection';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';

export function RegionalSection() {
  const { t } = useTranslation();
  const { orgId, org, online, canEdit } = useSettingsAccess();
  const { raw, loading, error } = useOrgSettingsDoc(orgId, 'regional', canEdit || Boolean(orgId));
  const { saving, run } = useAsyncSave('settings-regional');

  const [showHijri, setShowHijri] = useState(false);
  const [branchCode, setBranchCode] = useState('HQ');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!raw) return;
    setShowHijri(Boolean(raw.showHijri));
    setBranchCode(String(raw.branchCode ?? 'HQ'));
    setDirty(false);
  }, [raw]);

  useUnsavedWarning(dirty);

  const baseline = useMemo(
    () => ({
      showHijri: Boolean(raw?.showHijri),
      branchCode: String(raw?.branchCode ?? 'HQ')
    }),
    [raw]
  );

  function markDirty(nextHijri: boolean, nextBranch: string) {
    setShowHijri(nextHijri);
    setBranchCode(nextBranch);
    setDirty(nextHijri !== baseline.showHijri || nextBranch !== baseline.branchCode);
  }

  async function save() {
    if (!orgId || !online || !canEdit) return;
    await run(async () => {
      await setDoc(
        doc(getFirestoreDb(), 'organizations', orgId, 'settings', 'regional'),
        {
          showHijri,
          branchCode: branchCode.trim() || 'HQ',
          countryPack: org?.countryTemplate ?? 'generic',
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
      setDirty(false);
    }, t('settings.regionalSaved'));
  }

  return (
    <SettingsSection id="settings-regional" lazy title={t('settings.regional')} loading={loading}>
      {(visible) =>
        visible ? (
          <>
            <SettingsFirestoreError message={error ?? undefined} />
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={showHijri}
                onChange={(e) => markDirty(e.target.checked, branchCode)}
                disabled={!online || !canEdit}
              />
              {t('settings.showHijri')}
            </label>
            <div>
              <Label htmlFor="branch-code">{t('settings.branchCode')}</Label>
              <Input
                id="branch-code"
                className="mt-1 max-w-md"
                value={branchCode}
                onChange={(e) => markDirty(showHijri, e.target.value)}
                disabled={!online || !canEdit}
              />
            </div>
            {canEdit && (
              <Button
                variant="secondary"
                disabled={!online || !orgId || saving || !dirty}
                onClick={() => void save()}
              >
                {t('common.save')}
              </Button>
            )}
          </>
        ) : null
      }
    </SettingsSection>
  );
}
